import React, { useEffect, useState } from 'react';
import { Menu, X, Phone, UserCircle, ChevronDown } from 'lucide-react';
import { Button } from './ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { getNavTheme } from '../utils/navTheme';
export function Header() {
  const {
    pathname
  } = useLocation();
  const theme = getNavTheme(pathname);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);
  // Handle scroll effect for shadow
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdown(null);
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);
  const classButtons = [{
    name: 'In-Person Classes',
    subtitle: '(Live-classroom)',
    href: '/LiveClasses.html',
    color: 'bg-[#F5A623] hover:bg-[#E09612]'
  }, {
    name: 'Zoom Classes',
    subtitle: '(Live-virtual)',
    href: '/LiveClasses.html',
    color: 'bg-[#F5A623] hover:bg-[#E09612]'
  }, {
    name: 'On-Demand',
    subtitle: '(Self-Paced 24/7)',
    href: '/Products',
    color: 'bg-[#F5A623] hover:bg-[#E09612]'
  }];
  const navigationMenu = [{
    name: 'Home',
    href: theme.homeHref,
    type: 'link'
  }, {
    name: 'Classes',
    type: 'dropdown',
    items: [{
      name: 'On-Demand CE - Self Paced 24/7',
      href: '/Products'
    }, {
      name: 'Zoom Classes - Live-virtual',
      href: '/LiveClasses.html'
    }, {
      name: 'In-Person Classes - Live-classroom',
      href: '/LiveClasses.html'
    }]
  }, {
    name: 'License Information',
    type: 'dropdown',
    items: [{
      name: 'License Information',
      href: '/License_Information'
    }, {
      name: 'How To Report Classes To ADRE',
      href: '/How_To_Add_CE_Classes'
    }]
  }, {
    name: 'Blog',
    href: '/Blog',
    type: 'link'
  }, {
    name: 'About',
    type: 'dropdown',
    items: [{
      name: 'About Desert Sage Seminars',
      href: '/About'
    }, {
      name: 'Desert Sage Seminars Instructors',
      href: '/Instructors'
    }]
  }, {
    name: 'Help Zone',
    type: 'dropdown',
    items: [{
      name: 'FAQs (Frequently Asked Questions)',
      href: '/16.html'
    }, {
      name: 'Lost Certificates',
      href: '/11.html'
    }, {
      name: 'Reset Password',
      href: '/14.html'
    }, {
      name: 'Refund Policy',
      href: '/45.html'
    }, {
      name: 'Privacy Statement',
      href: '/15.html'
    }, {
      name: 'Contact Us',
      href: '/Contact'
    }]
  }, {
    name: 'Links & Resources',
    type: 'dropdown',
    items: [{
      name: 'ADRE & Useful Links',
      href: '/Links&Resources'
    }, {
      name: 'Arizona Local Associations',
      href: '/Arizona_Local_Associations'
    }]
  }];
  const toggleMobileDropdown = (name: string) => {
    setOpenMobileDropdown(openMobileDropdown === name ? null : name);
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-md' : ''}`} data-id="element-290" data-mp="3bxgfvuv8ciwy">
      {/* Top Bar - SEO Text & Contact Info (Desktop Only) */}
      <div className="bg-brand-purple-900 text-white py-1.5 hidden lg:block" data-id="element-291" data-mp="27w716qdy1g8c">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center text-sm font-medium" data-id="element-292" data-mp="2cxwfcewgqa0n">
          <div className="text-white" data-id="element-293" data-mp="2gxwuk1rdyp8r">
            Arizona's Trusted ADRE-Approved Real Estate Continuing Education
            Provider
          </div>
          <div className="flex items-center space-x-8" data-id="element-294" data-mp="1p6qybwesdbor">
            <div className="flex items-center space-x-2 text-[#F5A623]" data-id="element-295" data-mp="1p56467wdvurf">
              <Phone className="w-4 h-4" data-id="element-296" data-mp="1t82gxhz8swng" />
              <span data-id="element-297" data-mp="1t716nfq1we6s">Customer Service:</span>
              <span className="font-bold text-white" data-id="element-298" data-mp="1y0m3v5jtl44x">602-377-6207</span>
            </div>
            <a href="/Contact" className="text-white hover:text-[#F5A623] transition-colors" data-id="element-299" data-mp="11ihgt6olva3f">
              Contact Us
            </a>
          </div>
        </div>
      </div>

      {/* Main Header Bar - Logo + Class Buttons + Login */}
      <div className="bg-white border-b border-gray-200" data-id="element-300" data-mp="t0xcspkq9g49">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-301" data-mp="t1guc56gu2hr">
          <div className="flex justify-between items-center h-16 lg:h-18" data-id="element-302" data-mp="oyap90v3ahcv">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center" data-id="element-303" data-mp="jyxu7su2e3z7">
              <a href={theme.homeHref} className="flex items-center group" data-id="element-304" data-mp="f0wcpccaecpu">
                <img src="https://cdn.magicpatterns.com/uploads/6EDXC2FVxZjvEzYwwvW1XK/image.png" alt="Desert Sage Seminars" className="h-12 lg:h-14 w-auto" data-id="element-305" data-mp="f2zaiijola54" />
              </a>
            </div>

            {/* Desktop - Class Buttons + Login */}
            <div className="hidden lg:flex items-center space-x-6" data-id="element-306" data-mp="3sfsz1duf9yfx">
              {/* Class Buttons */}
              <div className="flex items-center space-x-3" data-id="element-307" data-mp="3ngpq3ibbn82o">
                {classButtons.map(button => <a key={button.name} href={button.href} className={`px-5 py-2 flex flex-col items-center justify-center text-center rounded-md shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 border border-[#E09612]/20 ${button.color}`} data-id="element-308" data-mp="3dtdjoiyjay7b">
                    <span className="text-sm font-bold text-brand-purple-900" data-id="element-309" data-mp="38uiyjad3gxk1">
                      {button.name}
                    </span>
                    <span className="text-xs font-medium text-brand-purple-900/80" data-id="element-310" data-mp="5dahutu3s3qw">
                      {button.subtitle}
                    </span>
                  </a>)}
              </div>

              <div className="h-10 w-px bg-gray-200 mx-2" data-id="element-311" data-mp="3rslfr2zehq7a"></div>

              {/* Login Button */}
              <Button className="bg-brand-purple-600 hover:bg-brand-purple-700 text-white font-bold px-6 h-[52px] shadow-md hover:shadow-lg flex items-center gap-2 border border-brand-purple-700" data-id="element-312" data-mp="jdbhk5ymt54s">
                <UserCircle className="w-5 h-5" data-id="element-313" data-mp="28txa686if3o6" />
                <a href="/login-register" data-id="element-314" data-mp="28sm6e8d2dvx2">Log In / Register</a>
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center" data-id="element-315" data-mp="2ijqp9j5v20x6">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className={`inline-flex items-center justify-center px-3 py-2 border rounded-md text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white ${theme.toggle}`} data-id="element-316" data-mp="2ij74luotsyfg">
                <span className="font-bold mr-2" data-id="element-317" data-mp="1yw15nz82v6z1">Menu</span>
                {isMenuOpen ? <X className="block h-6 w-6" aria-hidden="true" data-id="element-318" data-mp="1ytofwzw2vsbf" /> : <Menu className="block h-6 w-6" aria-hidden="true" data-id="element-319" data-mp="1uwsb4s57r52f" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Menu Bar - Desktop Only */}
      <div className={`hidden lg:block border-t-2 border-b border-t-[#F5A623] shadow-sm ${theme.bar}`} data-id="element-320" data-mp="13mxldtytbtsd">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-321" data-mp="18lh31i6dijex">
          <nav className="flex items-center justify-center space-x-1 py-2" data-id="element-322" data-mp="18jnwe2vrfz6x">
            {navigationMenu.map(item => <div key={item.name} className="relative" data-id="element-323" data-mp="1ibjzxtgm8hij">
                {item.type === 'link' ? <a href={item.href} className={`block px-4 py-2 text-base font-bold text-white hover:text-white rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 ${theme.itemHover} ${theme.ringOffset}`} data-id="element-324" data-mp="1mdx0m3gi9p8g">
                    {item.name}
                  </a> : <div className="relative" onMouseEnter={() => setOpenDropdown(item.name)} onMouseLeave={() => setOpenDropdown(null)} data-id="element-325" data-mp="1ybyc12r53p9">
                    <button className={`flex items-center px-4 py-2 text-base font-bold text-white rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 ${theme.itemHover} ${theme.ringOffset} ${openDropdown === item.name ? theme.itemOpen : ''}`} onClick={e => {
                e.stopPropagation();
                setOpenDropdown(openDropdown === item.name ? null : item.name);
              }} data-id="element-326" data-mp="2k7q06qmxuwst">
                      {item.name}
                      <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${openDropdown === item.name ? 'rotate-180' : ''}`} data-id="element-327" data-mp="2u6xcdahxslec" />
                    </button>

                    <AnimatePresence data-id="element-328" data-mp="1dqufevjiu2cv">
                      {openDropdown === item.name && <motion.div initial={{
                  opacity: 0,
                  y: -10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.2
                }} className="absolute left-0 mt-1 w-72 bg-white rounded-md shadow-lg border border-gray-100 py-2 z-50" data-id="element-329" data-mp="1d2xgphqlaecf">
                          {item.items?.map(subItem => <a key={subItem.name} href={subItem.href} className="block px-4 py-2.5 text-base text-gray-700 hover:bg-brand-purple-50 hover:text-brand-purple-700 transition-colors" data-id="element-330" data-mp="20qfepv0g68nu">
                              {subItem.name}
                            </a>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}
              </div>)}
          </nav>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence data-id="element-331" data-mp="1za9uifxhpgo6">
        {isMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="lg:hidden bg-white border-t border-gray-100 overflow-hidden shadow-lg max-h-[calc(100vh-5rem)] overflow-y-auto" data-id="element-332" data-mp="1zdpcbqijt3ta">
            <div className="px-4 pt-4 pb-6 space-y-2" data-id="element-333" data-mp="3oqj46ejs4vnr">
              {/* SEO Text - Mobile */}
              <div className="text-center text-sm font-medium text-brand-purple-900 pb-3 mb-3 border-b border-gray-100" data-id="element-334" data-mp="3n9dnyw8agt3i">
                Arizona's Trusted ADRE-Approved Real Estate Continuing Education
                Provider
              </div>

              {/* Class Buttons - Mobile */}
              <div className="space-y-2 pb-3 mb-3 border-b border-gray-100" data-id="element-335" data-mp="38ccyz0hhfc8q">
                {classButtons.map(button => <a key={button.name} href={button.href} className={`block px-4 py-2 rounded-md text-center shadow-md border border-[#E09612]/20 ${button.color}`} onClick={() => setIsMenuOpen(false)} data-id="element-336" data-mp="9sw9tcv842j9">
                    <div className="text-base font-bold text-brand-purple-900" data-id="element-337" data-mp="3ra5zjw4mva2y">
                      {button.name}
                    </div>
                    <div className="text-sm font-medium text-brand-purple-900/80" data-id="element-338" data-mp="ntyvhoib3atn">
                      {button.subtitle}
                    </div>
                  </a>)}
              </div>

              {/* Navigation Menu - Mobile */}
              <div className={`border border-t-2 border-t-[#F5A623] rounded-lg p-2 space-y-1 ${theme.mobileGroup}`} data-id="element-339" data-mp="104zplbnael0x">
              {navigationMenu.map(item => <div key={item.name} data-id="element-340" data-mp="1a36zjaj5s2tf">
                  {item.type === 'link' ? <a href={item.href} className={`block px-4 py-3 text-base font-bold text-white hover:text-white rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white ${theme.itemHover}`} onClick={() => setIsMenuOpen(false)} data-id="element-341" data-mp="1f0fl7l3imozf">
                      {item.name}
                    </a> : <div data-id="element-342" data-mp="22r2vue4tfspo">
                      <button onClick={() => toggleMobileDropdown(item.name)} className={`flex items-center justify-between w-full px-4 py-3 text-base font-bold text-white rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white ${theme.itemHover} ${openMobileDropdown === item.name ? theme.itemOpen : ''}`} data-id="element-343" data-mp="22q9xo1jxabj5">
                        {item.name}
                        <ChevronDown className={`h-4 w-4 transition-transform ${openMobileDropdown === item.name ? 'rotate-180' : ''}`} data-id="element-344" data-mp="2gjc6zdvcz5zj" />
                      </button>

                      <AnimatePresence data-id="element-345" data-mp="2pkiqzwx7uyxj">
                        {openMobileDropdown === item.name && <motion.div initial={{
                    opacity: 0,
                    height: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto'
                  }} exit={{
                    opacity: 0,
                    height: 0
                  }} className="ml-4 mt-1 space-y-1 overflow-hidden" data-id="element-346" data-mp="2lhm3brstkcmq">
                            {item.items?.map(subItem => <a key={subItem.name} href={subItem.href} className={`block px-4 py-2 text-sm hover:text-white rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white ${theme.mobileSubText} ${theme.itemHover}`} onClick={() => setIsMenuOpen(false)} data-id="element-347" data-mp="r2glngb1wm6r">
                                {subItem.name}
                              </a>)}
                          </motion.div>}
                      </AnimatePresence>
                    </div>}
                </div>)}
              </div>

              {/* Contact & Login - Mobile */}
              <div className="pt-4 mt-4 border-t border-gray-100 space-y-3" data-id="element-348" data-mp="1fm0g5r2dahmo">
                <div className="flex items-center justify-center text-brand-purple-900 bg-gray-50 py-3 rounded-lg" data-id="element-349" data-mp="1ap1nze29kkr4">
                  <Phone className="w-5 h-5 mr-2 text-[#F5A623]" data-id="element-350" data-mp="15pxj2zmdh96l" />
                  <span className="font-medium mr-2 text-[#F5A623]" data-id="element-351" data-mp="10xxbqm3z3dzz">
                    Customer Service:
                  </span>
                  <span className="font-bold" data-id="element-352" data-mp="w05m5yqsw3yy">602-377-6207</span>
                </div>

                <Button className="w-full bg-brand-purple-600 hover:bg-brand-purple-700 text-white font-bold py-3 flex justify-center items-center gap-2" onClick={() => setIsMenuOpen(false)} data-id="element-353" data-mp="rysedj45dqpe">
                  <UserCircle className="w-5 h-5" data-id="element-354" data-mp="i1nyg10warh1" />
                  <a href="/login-register" data-id="element-355" data-mp="2tlv9sxvmdl7q">Log In / Register</a>
                </Button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </header>;
}